import React, { useEffect, useState, useContext } from 'react';
import PropTypes from 'prop-types';
import { ChevronLeft, ChevronRight, Loader2, ChevronDown, ChevronRight as ChevronRightExpand, ChevronUp, ArrowUpDown } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '~/shadcn/components/ui/Table';
import { Badge } from '~/shadcn/components/ui/Badge';
import { Button } from '~/shadcn/components/ui/Button';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '~/shadcn/components/ui/Select';
import { WarehouseInventoryV2Api } from '../../../../../../api/warehouseInventoryV2';
import { UserContext } from '../../../../../../contexts/UserContext';
import { BulkMovePalletsExpansionTable } from './BulkMovePalletsExpansionTable';
import { BulkMovePalletActionBar } from './BulkMovePalletActionBar';
import { BulkMovePalletsConfirmation } from './BulkMovePalletsConfirmation';

const warehouseInventoryV2Api = new WarehouseInventoryV2Api();

const BulkMovePalletsAction = ({ locationId, locationName, onSuccess, onCancel, lotId }) => {
    const [pallets, setPallets] = useState([]);
    const [filteredPallets, setFilteredPallets] = useState([]);
    const [loadingPallets, setLoadingPallets] = useState(false);
    const [destinationLocation, setDestinationLocation] = useState('');
    const [destinationLocationName, setDestinationLocationName] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [expandedRows, setExpandedRows] = useState(new Set());
    const [selectedPallets, setSelectedPallets] = useState(new Set());
    const [showConfirmation, setShowConfirmation] = useState(false);
    const { user } = useContext(UserContext);

    // Add sorting state
    const [sortConfig, setSortConfig] = useState({
        key: null,
        direction: 'asc'
    });

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // Sort function
    const sortData = (data, sortKey, direction) => {
        if (!sortKey) return data;

        return [...data].sort((a, b) => {
            let aValue = a[sortKey];
            let bValue = b[sortKey];

            // Handle numeric fields
            if (['numCases', 'numLooseUnits', 'totalUnits'].includes(sortKey)) {
                aValue = Number(aValue);
                bValue = Number(bValue);
            }

            if (aValue < bValue) return direction === 'asc' ? -1 : 1;
            if (aValue > bValue) return direction === 'asc' ? 1 : -1;
            return 0;
        });
    };

    // Handle sort
    const handleSort = (key) => {
        setSortConfig((prevSort) => ({
            key,
            direction: prevSort.key === key && prevSort.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    // Update the pagination and sorting logic to use filtered pallets
    const sortedPallets = sortData(filteredPallets, sortConfig.key, sortConfig.direction);
    const totalItems = sortedPallets.length;
    const totalPages = Math.ceil(totalItems / rowsPerPage);
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    const currentItems = sortedPallets.slice(startIndex, endIndex);

    // Add effect to reset pagination when search query changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery]);

    const getPallets = async () => {
        setLoadingPallets(true);
        const filters = [
            { searchBy: 'locationId', searchTerm: locationId },
        ];

        if (lotId) {
            filters.push({ searchBy: 'lotId', searchTerm: lotId });
        }

        const res = await warehouseInventoryV2Api.getPallets(user.facilityID, filters);
        setLoadingPallets(false);

        if (!res.status) {
            return;
        }

        setPallets(res.data.pallets);
    };

    const toggleRow = (palletId) => {
        const newExpandedRows = new Set(expandedRows);
        if (newExpandedRows.has(palletId)) {
            newExpandedRows.delete(palletId);
        } else {
            newExpandedRows.add(palletId);
        }
        setExpandedRows(newExpandedRows);
    };

    useEffect(() => {
        if (!locationId) return;
        getPallets();
    }, [locationId]);

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            const allPalletIds = new Set(pallets.map(pallet => pallet.palletId));
            setSelectedPallets(allPalletIds);
        } else {
            setSelectedPallets(new Set());
        }
    };

    const handleSelectRow = (palletId) => {
        const newSelected = new Set(selectedPallets);
        if (newSelected.has(palletId)) {
            newSelected.delete(palletId);
        } else {
            newSelected.add(palletId);
        }
        setSelectedPallets(newSelected);
    };

    const areAllSelected = () => {
        return pallets.length > 0 && selectedPallets.size === pallets.length;
    };

    const areSomeSelected = () => {
        return selectedPallets.size > 0 && selectedPallets.size < pallets.length;
    };

    const handleConfirmMove = () => {
        if (selectedPallets.size === 0) {
            return;
        }
        setShowConfirmation(true);
    };

    const handleDestinationLocationChange = (locationId, locationName) => {
        setDestinationLocation(locationId);
        setDestinationLocationName(locationName);
    };

    if (showConfirmation) {
        return (
            <BulkMovePalletsConfirmation
                selectedPalletIds={Array.from(selectedPallets)}
                destinationLocationId={destinationLocation}
                destinationLocationName={destinationLocationName}
                onSuccess={onSuccess}
                onCancel={onCancel}
                onGoBack={() => setShowConfirmation(false)}
            />
        );
    }

    const StatusBadge = ({ status }) => {
        const isATS = status === 'ATS';
        const badgeClass = isATS
            ? 'bg-green-100 text-green-700 hover:bg-green-100'
            : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-100';
        return <Badge className={`${badgeClass} rounded-md font-semibold`}>{status}</Badge>;
    };

    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
    };

    const handleRowsPerPageChange = (value) => {
        setRowsPerPage(Number(value));
        setCurrentPage(1);
    };

    return (
        <div style={{ 
            width: '100%',
            minWidth: '600px',
            height: '100%',
            minHeight: '400px',
            display: 'flex',
            flexDirection: 'column',
            margin: '0 auto'
        }}>
            <div className="px-4 py-3 border-b">
                <h2 className="text-xl font-semibold text-gray-900">
                    {lotId 
                        ? `Pallets for Lot ${lotId} in Location ${locationName || locationId}`
                        : `All Pallets in Location ${locationName || locationId}`
                    }
                </h2>
            </div>
            <BulkMovePalletActionBar 
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                selectedCount={selectedPallets.size}
                pallets={pallets}
                onPalletsFiltered={setFilteredPallets}
                destinationLocation={destinationLocation}
                onDestinationLocationChange={handleDestinationLocationChange}
                onMove={handleConfirmMove}
                isLoading={loadingPallets}
                facilityID={user.facilityID}
            />

            <div style={{ 
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column'
            }}>
                <div style={{ 
                    flex: 1,
                    overflow: 'auto',
                    padding: '0 16px',
                    minHeight: '200px'
                }}>
                    {loadingPallets ? (
                        <div className="flex flex-col items-center justify-center h-full py-10">
                            <Loader2 className="h-8 w-8 animate-spin text-gray-400 mb-3" />
                            <p className="text-sm text-gray-600">Loading pallets...</p>
                        </div>
                    ) : pallets.length === 0 ? (
                        <div className="text-center py-10">
                            <p className="text-lg font-semibold">No Pallets Found</p>
                            <p className="text-sm text-gray-600">
                                There are no pallets in location {locationName || locationId}.
                            </p>
                        </div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow className="hover:bg-transparent">
                                    <TableHead className="w-12 bg-white sticky top-0 text-center">
                                        <input
                                            type="checkbox"
                                            checked={areAllSelected()}
                                            ref={(el) => {
                                                if (el) {
                                                    el.indeterminate = areSomeSelected();
                                                }
                                            }}
                                            onChange={handleSelectAll}
                                        />
                                    </TableHead>
                                    <TableHead
                                        className="bg-white sticky top-0 cursor-pointer hover:bg-gray-50 text-center"
                                        onClick={() => handleSort('palletId')}
                                    >
                                        <div className="flex items-center gap-1 justify-center">
                                            PALLET
                                            {sortConfig.key === 'palletId' ? (
                                                sortConfig.direction === 'asc' ?
                                                    <ChevronUp className="h-4 w-4" /> :
                                                    <ChevronDown className="h-4 w-4" />
                                            ) : (
                                                <ArrowUpDown className="h-4 w-4 text-gray-400" />
                                            )}
                                        </div>
                                    </TableHead>
                                    <TableHead
                                        className="bg-white sticky top-0 cursor-pointer hover:bg-gray-50 text-center"
                                        onClick={() => handleSort('numCases')}
                                    >
                                        <div className="flex items-center gap-1 justify-center">
                                            CASES
                                            {sortConfig.key === 'numCases' ? (
                                                sortConfig.direction === 'asc' ?
                                                    <ChevronUp className="h-4 w-4" /> :
                                                    <ChevronDown className="h-4 w-4" />
                                            ) : (
                                                <ArrowUpDown className="h-4 w-4 text-gray-400" />
                                            )}
                                        </div>
                                    </TableHead>
                                    <TableHead
                                        className="bg-white sticky top-0 cursor-pointer hover:bg-gray-50 text-center"
                                        onClick={() => handleSort('numLooseUnits')}
                                    >
                                        <div className="flex items-center gap-1 justify-center">
                                            LOOSE UNITS
                                            {sortConfig.key === 'numLooseUnits' ? (
                                                sortConfig.direction === 'asc' ?
                                                    <ChevronUp className="h-4 w-4" /> :
                                                    <ChevronDown className="h-4 w-4" />
                                            ) : (
                                                <ArrowUpDown className="h-4 w-4 text-gray-400" />
                                            )}
                                        </div>
                                    </TableHead>
                                    <TableHead
                                        className="bg-white sticky top-0 cursor-pointer hover:bg-gray-50 text-center"
                                        onClick={() => handleSort('totalUnits')}
                                    >
                                        <div className="flex items-center gap-1 justify-center">
                                            TOTAL UNITS
                                            {sortConfig.key === 'totalUnits' ? (
                                                sortConfig.direction === 'asc' ?
                                                    <ChevronUp className="h-4 w-4" /> :
                                                    <ChevronDown className="h-4 w-4" />
                                            ) : (
                                                <ArrowUpDown className="h-4 w-4 text-gray-400" />
                                            )}
                                        </div>
                                    </TableHead>
                                    <TableHead
                                        className="bg-white sticky top-0 cursor-pointer hover:bg-gray-50 text-center"
                                        onClick={() => handleSort('status')}
                                    >
                                        <div className="flex items-center gap-1 justify-center">
                                            STATUS
                                            {sortConfig.key === 'status' ? (
                                                sortConfig.direction === 'asc' ?
                                                    <ChevronUp className="h-4 w-4" /> :
                                                    <ChevronDown className="h-4 w-4" />
                                            ) : (
                                                <ArrowUpDown className="h-4 w-4 text-gray-400" />
                                            )}
                                        </div>
                                    </TableHead>
                                    <TableHead className="bg-white sticky top-0 w-20 text-center">DETAILS</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {currentItems.map((pallet) => (
                                    <React.Fragment key={pallet.palletId}>
                                        <TableRow>
                                            <TableCell className="w-12 text-center">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedPallets.has(pallet.palletId)}
                                                    onChange={() => handleSelectRow(pallet.palletId)}
                                                />
                                            </TableCell>
                                            <TableCell className="text-center">{pallet.palletId}</TableCell>
                                            <TableCell className="text-center">{pallet.numCases}</TableCell>
                                            <TableCell className="text-center">{pallet.numLooseUnits}</TableCell>
                                            <TableCell className="text-center">{pallet.totalUnits}</TableCell>
                                            <TableCell className="text-center">
                                                <div className="flex justify-center">
                                                    <StatusBadge status={pallet.status} />
                                                </div>
                                            </TableCell>
                                            <TableCell className="text-center">
                                                <div className="flex justify-center">
                                                    <Button
                                                        variant="ghost"
                                                        className="h-8 w-8 p-0"
                                                        onClick={() => toggleRow(pallet.palletId)}
                                                    >
                                                        {expandedRows.has(pallet.palletId) ? (
                                                            <ChevronDown className="h-4 w-4" />
                                                        ) : (
                                                            <ChevronRightExpand className="h-4 w-4" />
                                                        )}
                                                        <span className="sr-only">Toggle Details</span>
                                                    </Button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                        {expandedRows.has(pallet.palletId) && (
                                            <TableRow>
                                                <TableCell colSpan={7} className="p-0">
                                                    <BulkMovePalletsExpansionTable
                                                        palletId={pallet.palletId}
                                                        onClose={() => toggleRow(pallet.palletId)}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </React.Fragment>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </div>

                {/* Pagination container */}
                {pallets.length > 0 && (
                    <div className="border-t p-4 bg-white">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <Select
                                    value={rowsPerPage}
                                    onValueChange={handleRowsPerPageChange}
                                >
                                    <SelectTrigger className="h-8 w-[70px]">
                                        <SelectValue placeholder={rowsPerPage} />
                                    </SelectTrigger>
                                    <SelectContent side="top">
                                        {[10, 20, 30, 40, 50].map((pageSize) => (
                                            <SelectItem key={pageSize} value={pageSize}>
                                                {pageSize}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <span className="text-sm text-gray-600">
                                    rows per page
                                </span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="text-sm text-gray-600">
                                    {startIndex + 1}-{Math.min(endIndex, filteredPallets.length)} of {filteredPallets.length}
                                </span>
                                <div className="flex items-center gap-1">
                                    <Button
                                        variant="ghost"
                                        className="h-8 w-8 p-0"
                                        onClick={() => handlePageChange(currentPage - 1)}
                                        disabled={currentPage === 1}
                                    >
                                        <ChevronLeft className="h-4 w-4" />
                                        <span className="sr-only">Previous page</span>
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        className="h-8 w-8 p-0"
                                        onClick={() => handlePageChange(currentPage + 1)}
                                        disabled={endIndex >= filteredPallets.length}
                                    >
                                        <ChevronRight className="h-4 w-4" />
                                        <span className="sr-only">Next page</span>
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

BulkMovePalletsAction.propTypes = {
    locationId: PropTypes.string.isRequired,
    locationName: PropTypes.string,
    onSuccess: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    lotId: PropTypes.string
};

export { BulkMovePalletsAction };
